/**
 * Unified Workspace - Design Tokens
 * Sourced from DAGGPT brand palette (violet/indigo) used across ChatPage, Gallery, Rankings.
 */

export const TOKENS = {
  // Surfaces
  bg:       '#FFFFFF',
  canvas:   '#FAFAF7',
  sidebar:  '#FBFAF7',
  line:     '#EAEAE6',
  lineSoft: '#F2F1EC',

  // Text
  ink:    '#0A0A0A',
  ink2:   '#1C1C1C',
  muted:  '#71717A',
  subtle: '#A1A1AA',

  // Brand palette - DAGGPT violet/indigo (from existing codebase)
  primary:     '#7c3aed',   // violet-600
  primaryMid:  '#6d28d9',   // violet-700
  secondary:   '#4f46e5',   // indigo-600
  deep:        '#4c1d95',   // violet-900
  soft:        '#8b5cf6',   // violet-500
  light:       '#a78bfa',   // violet-400
  pale:        '#ede9fe',   // violet-100
  faint:       '#f5f3ff',   // violet-50

  // Signature gradient
  gradient: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',

  // Per-mode accents (subtle differentiation within family)
  modeColors: {
    chat:  '#7c3aed',
    image: '#6366f1',
    video: '#5b21b6',
    music: '#8b5cf6',
    slides: '#10B981',
  },

  // Status / feedback
  ok:    '#10B981',
  warn:  '#F59E0B',
  error: '#EF4444',

  // ─── Million-dollar redesign additions ───
  // Glass surfaces (translucent, used with backdrop-filter: blur+saturate)
  glass:        'rgba(255,255,255,0.62)',
  glassStrong:  'rgba(255,255,255,0.82)',
  glassDark:    'rgba(20,16,32,0.62)',
  glassDarker:  'rgba(12,10,20,0.78)',

  // Tiered shadow system
  shadowT1: '0 1px 2px rgba(10,10,10,0.04)',
  shadowT2: '0 1px 2px rgba(10,10,10,0.04), 0 8px 22px -10px rgba(124,58,237,0.30)',
  shadowT3: '0 2px 6px rgba(10,10,10,0.05), 0 24px 60px -16px rgba(124,58,237,0.45)',
  shadowFocus: '0 0 0 4px rgba(167,139,250,0.16), 0 24px 60px -20px rgba(124,58,237,0.50)',

  // Glow tokens (use as box-shadow / drop-shadow accents)
  glowViolet:   '0 0 60px -10px rgba(124,58,237,0.55)',
  glowIndigo:   '0 0 60px -10px rgba(99,102,241,0.50)',
  glowPink:     '0 0 60px -10px rgba(236,72,153,0.45)',
  glowAurora:   '0 0 80px -10px rgba(167,139,250,0.50), 0 0 120px -20px rgba(236,72,153,0.30)',
};

/**
 * ATELIER - the single committed LIGHT theme for the unified workspace.
 * Pure white surfaces with violet light-bleed shadows.
 * Inspired by Linear-light, Vercel, Granola, refined Apple whites.
 *
 * Surfaces never use grey for grey's sake - they use **violet-tinted neutrals**
 * so the whole UI feels lit by a soft purple light source.
 */
export const ATELIER = {
  // ─── Surfaces (5 levels of luminance) ────────────────────────
  bg:        '#FFFFFF',  // pure white app shell + thread canvas
  bgSubtle:  '#FAFAFB',  // sidebar tint (almost-white)
  bgRaised:  '#FFFFFF',  // cards (use shadow for elevation, not bg)
  bgHover:   '#F4F1FA',  // hover state - violet-tinted
  bgActive:  '#EDE7FB',  // pressed / active row - stronger violet tint
  bgInk:     '#0A0A14',  // INVERSE: rare full-bleed dark surface (e.g. CTA pill)

  // ─── Borders - hairline, violet-grey ────────────────────────
  border:       '#E8E5F0',  // primary hairline
  borderSoft:   '#F1EEF8',  // even softer divider
  borderStrong: '#D9D3E8',  // hover + focus borders
  borderAccent: 'rgba(124,58,237,0.22)',  // violet-tinted (focus rings)

  // ─── Ink ─────────────────────────────────────────────────────
  ink:      '#0A0A14',  // primary text, headlines
  inkMid:   '#3A3650',  // body text
  inkSoft:  '#6B6680',  // captions, secondary
  inkMuted: '#A19BB5',  // metadata, placeholders
  inkFaint: '#C8C2D6',  // disabled, separators-as-text

  // ─── Accent (violet family) ──────────────────────────────────
  accent:        '#7C3AED',  // primary violet - links, active states
  accentHover:   '#6D28D9',  // hover on primary
  accentDeep:    '#5B21B6',  // pressed
  accentLight:   '#A78BFA',  // sub-accent, ribbons
  accentSoft:    '#F4EEFE',  // tinted bg for active rows / chips
  accentSoftHover: '#EBE0FE',
  accentRing:    'rgba(124,58,237,0.18)',  // focus ring overlay

  // ─── Status ──────────────────────────────────────────────────
  ok:    '#059669',
  warn:  '#D97706',
  error: '#DC2626',

  // ─── Brand gradient (CTAs only - never decorative) ──────────
  gradient: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
  gradientHover: 'linear-gradient(135deg, #6D28D9 0%, #4338CA 100%)',

  // ─── Aurora gradient (for the composer ribbon, memory ribbons) ──
  aurora: 'linear-gradient(120deg, #A78BFA 0%, #C4B5FD 25%, #F0ABFC 50%, #C4B5FD 75%, #A78BFA 100%)',

  // ─── Shadows - violet-tinted, no grey ───────────────────────
  shadowS:  '0 1px 2px rgba(124,58,237,0.06)',
  shadowM:  '0 4px 14px -4px rgba(124,58,237,0.14), 0 1px 2px rgba(10,10,20,0.04)',
  shadowL:  '0 12px 32px -10px rgba(124,58,237,0.20), 0 2px 4px rgba(10,10,20,0.04)',
  shadowXL: '0 24px 60px -16px rgba(124,58,237,0.28), 0 4px 12px rgba(10,10,20,0.05)',
  shadowFocus: '0 0 0 4px rgba(124,58,237,0.14)',

  // ─── Per-modality accent (within violet family) ──────────────
  modes: {
    chat:  { tint: '#F4EEFE', accent: '#7C3AED', label: 'Chat'  },
    image: { tint: '#EEF0FE', accent: '#5B6CF8', label: 'Image' },
    video: { tint: '#F1ECFE', accent: '#6E3FE3', label: 'Video' },
    music: { tint: '#F8ECFC', accent: '#B042D9', label: 'Music' },
    slides: { tint: '#ECFDF5', accent: '#10B981', label: 'Slides' },
  },
};

/** Motion language */
export const MOTION = {
  ease: {
    softExpo: [0.22, 1, 0.36, 1],
    spring:   [0.34, 1.36, 0.64, 1],
    glide:    [0.4, 0, 0.2, 1],
  },
  duration: {
    fast: 0.18,
    std:  0.32,
    slow: 0.6,
    cinematic: 1.1,
  },
};

export const FONTS = {
  serif: '"Nasalization", "Instrument Serif", Georgia, serif',
  sans:  '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  mono:  '"JetBrains Mono", ui-monospace, Menlo, monospace',
};

/** Mode registry - master list used by the unified workspace */
export const MODES = [
  { id: 'chat',  label: 'Chat',  slashCmd: 'chat',  color: TOKENS.modeColors.chat  },
  { id: 'image', label: 'Image', slashCmd: 'image', color: TOKENS.modeColors.image },
  { id: 'video', label: 'Video', slashCmd: 'video', color: TOKENS.modeColors.video },
  { id: 'music', label: 'Music', slashCmd: 'music', color: TOKENS.modeColors.music },
  { id: 'slides', label: 'Slides', slashCmd: 'slides', color: TOKENS.modeColors.slides },
];

export const MODE_IDS = MODES.map(m => m.id);

/** Minimal inline SVG icon set (stroke-based, 24�-24) */
export const ICON = {
  chat:    'M21 12c0 4.418-4.03 8-9 8-1.25 0-2.44-.22-3.53-.62L3 21l1.67-4.5C3.61 15.04 3 13.58 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  image:   'M3 7.5A1.5 1.5 0 014.5 6h15A1.5 1.5 0 0121 7.5v9A1.5 1.5 0 0119.5 18h-15A1.5 1.5 0 013 16.5v-9zM8 11a2 2 0 100-4 2 2 0 000 4zM3 15.5l5-5 4 4 3-3 6 6',
  video:   'M4 6h12a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zM18 10l4-2v8l-4-2',
  music:   'M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zM21 16a3 3 0 11-6 0 3 3 0 016 0z',
  slides:  'M15 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7l-5-5zM14 3v4h4M8 12h8M8 16h8',
  gallery: 'M4 4h6v6H4V4zM14 4h6v6h-6V4zM4 14h6v6H4v-6zM14 14h6v6h-6v-6z',
  history: 'M12 8v4l3 2M3 12a9 9 0 109-9 9.75 9.75 0 00-6.75 2.75L3 8M3 3v5h5',
  chevron: 'M9 6l6 6-6 6',
  chevronDown: 'M6 9l6 6 6-6',
  send:    'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  plus:    'M12 5v14M5 12h14',
  back:    'M19 12H5M12 19l-7-7 7-7',
  attach:  'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48',
  search:  'M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z',
  sparkle: 'M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2zM19 14l.9 2.7L22 18l-2.1.3L19 21l-.9-2.7L16 18l2.1-.3L19 14zM5 14l.9 2.7L8 18l-2.1.3L5 21l-.9-2.7L2 18l2.1-.3L5 14z',
  ratio:   'M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z',
  clock:   'M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  palette: 'M12 3a9 9 0 100 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.2 0-1.1.9-2 2-2h2.3A4.7 4.7 0 0021 10c0-3.9-4-7-9-7zM7 12a1 1 0 110-2 1 1 0 010 2zM10 8a1 1 0 110-2 1 1 0 010 2zM14 7a1 1 0 110-2 1 1 0 010 2zM17 11a1 1 0 110-2 1 1 0 010 2z',
  mic:     'M12 2a3 3 0 00-3 3v6a3 3 0 006 0V5a3 3 0 00-3-3zM19 10v1a7 7 0 01-14 0v-1M12 18v4M8 22h8',
  check:   'M20 6L9 17l-5-5',
  cog:     'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
};

/**
 * MODELS - representative model registry per mode. Displayed in model picker.
 * (Phase 3 will wire the real selected model into the generation APIs.)
 */
// NOTE: These are FALLBACK model IDs used only if /api/models fails to load.
// Chat IDs must be real OpenRouter IDs (provider/model) so the live /api/chat
// endpoint accepts them. Image/video/music IDs are illustrative until those
// endpoints are wired.
export const MODELS = {
  chat: [
    { id: 'openai/gpt-4o',              label: 'GPT-4o',              badge: 'FLAGSHIP',  provider: 'OpenAI'    },
    { id: 'openai/gpt-4o-mini',         label: 'GPT-4o mini',         badge: 'FAST',      provider: 'OpenAI'    },
    { id: 'anthropic/claude-3.5-sonnet',label: 'Claude 3.5 Sonnet',   badge: 'REASONING', provider: 'Anthropic' },
    { id: 'google/gemini-2.5-pro',      label: 'Gemini 2.5 Pro',      badge: 'LONG CTX',  provider: 'Google'    },
    { id: 'meta-llama/llama-3.3-70b-instruct', label: 'Llama 3.3 70B',badge: 'OPEN',      provider: 'Meta'      },
  ],
  // Canonical 8 image models. IDs match Kie.AI route + backend mappings.
  image: [
    { id: 'gpt-image-2',       label: 'GPT Image 2',       badge: 'FLAGSHIP', provider: 'OpenAI' },
    { id: 'gpt-image-1.5',     label: 'GPT Image 1.5',     badge: 'BALANCED', provider: 'OpenAI' },
    { id: 'grok-imagine',      label: 'Grok Imagine',      badge: 'X',        provider: 'xAI'    },
    { id: 'nano-banana-pro',   label: 'Nano Banana Pro',   badge: 'FLAGSHIP', provider: 'Google' },
    { id: 'nano-banana-2',     label: 'Nano Banana 2',     badge: 'PREVIEW',  provider: 'Google' },
    { id: 'flux-2',            label: 'FLUX 2',            badge: 'FLEX',     provider: 'Black Forest' },
    { id: 'flux-2-pro',        label: 'FLUX 2 Pro',        badge: 'PRO',      provider: 'Black Forest' },
    { id: 'seedream-5.0-lite', label: 'Seedream 5.0 Lite', badge: 'LITE',     provider: 'ByteDance' },
  ],
  video: [
    { id: 'kling-3.0',         label: 'Kling 3.0',         badge: 'PRO',      provider: 'Kling' },
    { id: 'kling-2.6',         label: 'Kling 2.6',         badge: 'V2.6',     provider: 'Kling' },
    { id: 'grok-imagine',      label: 'Grok Imagine',      badge: 'X',        provider: 'xAI' },
    { id: 'seedance-2.0',      label: 'Seedance 2.0',      badge: 'NEW',      provider: 'ByteDance' },
    { id: 'seedance-2.0-fast', label: 'Seedance 2.0 Fast', badge: 'FAST',     provider: 'ByteDance' },
    { id: 'sora2-pro',         label: 'Sora2 Pro',         badge: 'PREMIUM',  provider: 'Sora2' },
    { id: 'sora2',             label: 'Sora2',             badge: 'BALANCED', provider: 'Sora2' },
    { id: 'happyhorse',        label: 'HappyHorse',        badge: 'NEW',      provider: 'HappyHorse' },
    { id: 'veo-3.1',           label: 'Veo 3.1',           badge: 'GOOGLE',   provider: 'Google' },
  ],
  music: [
    { id: 'suno-v4',         label: 'Suno v4',         badge: 'FLAGSHIP', provider: 'Suno' },
    { id: 'suno-v3.5',       label: 'Suno v3.5',       badge: 'FAST',     provider: 'Suno' },
  ],
};

/**
 * Model-specific capabilities for video generation.
 * Used to dynamically adjust the UI controls (durations, aspects, etc.) per model.
 */
export const VIDEO_CAPABILITIES = {
  'kling-3.0': {
    durations: [3, 5, 8, 10, 15],
    aspects: ['16:9', '9:16', '1:1'],
    resolutions: ['720p', '1080p'],
    modes: ['std', 'pro']
  },
  'kling-2.6': {
    durations: [5, 10],
    aspects: ['1:1', '16:9', '9:16'],
    resolutions: ['720p'],
    modes: ['normal']
  },
  'grok-imagine': {
    durations: [6, 10, 15, 20, 25, 30],
    aspects: ['2:3', '3:2', '1:1', '16:9', '9:16'],
    resolutions: ['480p', '720p'],
    modes: ['normal', 'fun', 'spicy']
  },
  'seedance-2.0': {
    durations: [4, 5, 8, 10, 12, 15],
    aspects: ['1:1', '4:3', '3:4', '16:9', '9:16', '21:9', 'adaptive'],
    resolutions: ['720p'],
    modes: ['normal']
  },
  'seedance-2.0-fast': {
    durations: [4, 5, 8, 10, 12, 15],
    aspects: ['1:1', '4:3', '3:4', '16:9', '9:16', '21:9', 'adaptive'],
    resolutions: ['720p'],
    modes: ['normal']
  },
  'sora2': {
    durations: [10, 15],
    aspects: ['landscape', 'portrait'],
    resolutions: ['720p'],
    modes: ['normal']
  },
  'sora2-pro': {
    durations: [10, 15],
    aspects: ['landscape', 'portrait'],
    resolutions: ['1080p'],
    modes: ['normal']
  },
  'happyhorse': {
    durations: [3, 5, 8, 10, 12, 15],
    aspects: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    resolutions: ['720p', '1080p'],
    modes: ['normal']
  },
  'veo-3.1': {
    durations: [3, 5, 8, 10, 15],
    aspects: ['16:9', '9:16', '1:1'],
    resolutions: ['720p', '1080p'],
    modes: ['normal']
  }
};

/**
 * MODE_CONTROLS - per-mode secondary control definitions rendered in the prompt bar.
 * Each control maps to a key on WorkspaceContext.modes[mode].
 */
export const MODE_CONTROLS = {
  chat: [
    { key: 'webSearch', icon: 'search', label: 'Search', format: (v) => v ? 'Search ON' : 'Search OFF', options: [
      { id: true,  label: 'Search ON' },
      { id: false, label: 'Search OFF' },
    ]}
  ],
  image: [
    { key: 'aspect', icon: 'ratio',   label: 'Aspect', options: [
      { id: '1:1',  label: '1:1 Square' },
      { id: '16:9', label: '16:9 Landscape' },
      { id: '9:16', label: '9:16 Portrait' },
      { id: '4:3',  label: '4:3 Classic' },
      { id: '3:2',  label: '3:2 Photo' },
    ]},
    { key: 'style',  icon: 'palette', label: 'Style', options: [
      { id: 'Photorealistic', label: 'Photorealistic' },
      { id: 'Cinematic',      label: 'Cinematic' },
      { id: 'Editorial',      label: 'Editorial' },
      { id: 'Illustration',   label: 'Illustration' },
      { id: '3D Render',      label: '3D Render' },
      { id: 'Anime',          label: 'Anime' },
    ]},
  ],
  video: [
    { key: 'aspect',   icon: 'ratio', label: 'Aspect', options: [
      { id: '2:3',  label: '2:3 Portrait' },
      { id: '3:2',  label: '3:2 Landscape' },
      { id: '1:1',  label: '1:1 Square' },
      { id: '16:9', label: '16:9 Widescreen' },
      { id: '9:16', label: '9:16 Tall' },
    ]},
    { key: 'duration', icon: 'clock', label: 'Duration', format: (v) => `${v}s`, options: [
      { id: 6,  label: '6s' },
      { id: 10, label: '10s' },
      { id: 15, label: '15s' },
      { id: 20, label: '20s' },
      { id: 30, label: '30s' },
    ]},
    { key: 'mode',    icon: 'palette', label: 'Motion', options: [
      { id: 'normal', label: 'Normal' },
      { id: 'fun',    label: 'Fun' },
      { id: 'spicy',  label: 'Spicy' },
    ]},
    { key: 'resolution', icon: 'gallery', label: 'Resolution', options: [
      { id: '480p', label: '480p' },
      { id: '720p', label: '720p' },
    ]},
  ],
  music: [
    { key: 'style', icon: 'palette', label: 'Genre', options: [
      { id: 'Pop',        label: 'Pop' },
      { id: 'Cinematic',  label: 'Cinematic' },
      { id: 'Lo-fi',      label: 'Lo-fi' },
      { id: 'Electronic', label: 'Electronic' },
      { id: 'Jazz',       label: 'Jazz' },
      { id: 'Ambient',    label: 'Ambient' },
    ]},
    { key: 'vocal', icon: 'mic', label: 'Vocals', format: (v) => v ? 'With vocals' : 'Instrumental', options: [
      { id: true,  label: 'With vocals' },
      { id: false, label: 'Instrumental' },
    ]},
  ],
};

/** Placeholder copy per mode (serif, display) */
export const MODE_PROMPT_PLACEHOLDER = {
  chat:  'Ask anything, or /command',
  image: 'Describe an image in detail…',
  video: 'Describe a scene - motion, mood, subject…',
  music: 'Describe the sound - genre, tempo, feel…',
};
